import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { useAuth } from '../auth/AuthProvider.js';

interface LocationState {
  /** Set by the reset-password page so a completed reset is confirmed on arrival. */
  notice?: string;
}

export function LoginPage(): ReactElement {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const notice = (location.state as LocationState | null)?.notice;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login({ email, password });

      /*
       * Defence in depth, and no longer the only thing enforcing this.
       *
       * The client sends `audience: 'backoffice'` (see AppProviders) and the API refuses a
       * non-admin that audience before minting a token at all — with the same 401 as a wrong
       * password, so nothing here can distinguish the two, which is the point. This branch
       * is therefore not expected to be reached through the form; it survives so that a
       * session restored from storage, or a future client that forgets the audience, still
       * cannot put a non-admin in front of the backoffice.
       */
      if (user.role !== 'admin') {
        logout();
        setError('This account does not have backoffice access.');
        return;
      }

      navigate('/interviews', { replace: true });
    } catch (cause) {
      setError(messageForError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-session-text">
          Backoffice sign in
        </h1>
        <p className="mt-1 mb-6 text-sm text-session-muted">Administrator accounts only.</p>

        <form onSubmit={onSubmit} className="panel space-y-4 p-5" noValidate>
          {notice ? (
            <p role="status" className="form-notice">
              {notice}
            </p>
          ) : null}

          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="email" className="field-label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="field-input"
            />
          </div>

          <div>
            <label htmlFor="password" className="field-label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="field-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-session-muted underline">
              Forgot your password?
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
